import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users, Plus, Trash2, Edit3, ExternalLink, RefreshCw,
  Key, Shield, Check, X, Loader2, ChevronRight, Building2,
  BarChart3, Globe, AlertTriangle, Lock, Mail, TrendingUp,
  CreditCard, Activity, ToggleRight, UserCog, Cookie, LineChart, Award, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import { LeadsCrmTab } from './superadmin/LeadsCrmTab';
import { MarketingTab } from './superadmin/MarketingTab';
import { BillingTab } from './superadmin/BillingTab';
import { SystemTab } from './superadmin/SystemTab';
import { FeatureFlagsTab } from './superadmin/FeatureFlagsTab';
import { AdminUsersTab } from './superadmin/AdminUsersTab';
import { SettingsTab } from './superadmin/SettingsTab';
import { AnalyticsTab } from './superadmin/AnalyticsTab';
import { CertifiedBrandsTab } from './superadmin/CertifiedBrandsTab';
import { MarketplaceTab } from './superadmin/MarketplaceTab';
import { WhitelabelTab } from './superadmin/WhitelabelTab';
import { TenantAccessModal } from './superadmin/TenantAccessModal';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/superadmin';

const PLAN_COLORS = {
  essenziale: '#6b7280',
  professional: '#c9a84c',
  elite: '#7c3aed',
  lifetime: '#059669',
};

const PLAN_LABELS = {
  essenziale: 'Essenziale',
  professional: 'Professional',
  elite: 'Elite',
  lifetime: 'Lifetime',
};

export default function SuperAdminPanel() {
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState('');
  const [key, setKey] = useState(''); // password field
  const [keyError, setKeyError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [storedKey, setStoredKey] = useState(() => sessionStorage.getItem('ostivex_sa_key') || '');
  const [activeTab, setActiveTab] = useState('tenants');
  const [preselectedLead, setPreselectedLead] = useState(null);

  useEffect(() => {
    if (storedKey) {
      setAuthed(true);
      loadData(storedKey);
    }
  }, []);

  const login = async () => {
    setLoading(true);
    setKeyError('');
    try {
      const { data } = await axios.post(`${API}/auth`, {
        email: email.trim(),
        password: key,
      });
      sessionStorage.setItem('ostivex_sa_key', data.token);
      setStoredKey(data.token);
      setAuthed(true);
      loadData(data.token);
    } catch {
      setKeyError('Email o password non validi.');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (k) => {
    const headers = { 'X-Superadmin-Key': k };
    // Carica ogni endpoint in modo isolato: se uno fallisce, gli altri continuano
    // a popolarsi. Questo evita che un'API che va in errore in produzione lasci
    // la dashboard "nera" con un TypeError su undefined.map.
    const results = await Promise.allSettled([
      axios.get(`${API}/tenants`, { headers }),
      axios.get(`${API}/stats`, { headers }),
      axios.get(`${API}/plans`, { headers }),
    ]);
    const [tRes, sRes, pRes] = results;

    if (tRes.status === 'fulfilled') {
      setTenants(Array.isArray(tRes.value?.data?.tenants) ? tRes.value.data.tenants : []);
    } else {
      setTenants([]);
      console.error('[SuperAdmin] tenants load failed:', tRes.reason);
    }
    if (sRes.status === 'fulfilled') {
      setStats(sRes.value?.data || null);
    } else {
      setStats(null);
      console.error('[SuperAdmin] stats load failed:', sRes.reason);
    }
    if (pRes.status === 'fulfilled') {
      setPlans(pRes.value?.data?.plans || {});
    } else {
      setPlans({});
      console.error('[SuperAdmin] plans load failed:', pRes.reason);
    }

    const failed = results.filter(r => r.status === 'rejected');
    if (failed.length === results.length) {
      // Tutte fallite → chiave scaduta o backend giù
      toast.error('Impossibile contattare il server. Riprova o rifai login.');
    } else if (failed.length > 0) {
      toast.error(`Alcuni dati non sono stati caricati (${failed.length}/${results.length}).`);
    }
  };

  const logout = () => {
    sessionStorage.removeItem('ostivex_sa_key');
    setAuthed(false);
    setStoredKey('');
    setKey('');
  };

  if (!authed) return <LoginScreen email={email} setEmail={setEmail} keyVal={key} setKey={setKey} onLogin={login} error={keyError} loading={loading} />;

  const handleComposeToLead = (lead) => {
    setPreselectedLead(lead);
    setActiveTab('marketing');
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--lux-bg)', color: 'var(--lux-text)' }}>
      {/* Header */}
      <div className="border-b px-8 h-16 flex items-center justify-between" style={{ borderColor: 'var(--lux-border-1)', background: 'var(--lux-surface-1)' }}>
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-sm flex items-center justify-center" style={{ background: 'var(--lux-gold)' }}>
            <Key size={14} color="#000" strokeWidth={2.5} />
          </div>
          <div>
            <span className="font-serif text-lg font-light">Shangrilux</span>
            <span className="text-xs ml-2" style={{ color: 'var(--lux-gold)' }}>SuperAdmin</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => loadData(storedKey)} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--lux-text-muted)' }}>
            <RefreshCw size={13} /> Aggiorna
          </button>
          <a href="https://francescorusso.work" target="_blank" rel="noreferrer" className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>
            francescorusso.work
          </a>
          <button onClick={logout} className="text-xs px-3 py-1.5 rounded" style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
            Esci
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b px-4 lg:px-8 overflow-x-auto" style={{ borderColor: 'var(--lux-border-1)', background: 'var(--lux-surface-1)' }}>
        <div className="flex gap-0 min-w-max">
          {[
            { id: 'tenants', label: 'Tenant', icon: Building2 },
            { id: 'billing', label: 'Billing', icon: CreditCard },
            { id: 'leads', label: 'Lead CRM', icon: Users },
            { id: 'analytics', label: 'Analytics', icon: LineChart },
            { id: 'marketing', label: 'Marketing', icon: Mail },
            { id: 'features', label: 'Feature Flags', icon: ToggleRight },
            { id: 'certified', label: 'Partner Cert.', icon: Award },
            { id: 'marketplace', label: 'Marketplace', icon: Globe },
            { id: 'whitelabel', label: 'White-label', icon: Palette },
            { id: 'system', label: 'Sistema', icon: Activity },
            { id: 'sa_users', label: 'Admin Utenti', icon: UserCog },
            { id: 'settings', label: 'Impostazioni AI', icon: Key },
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className="flex items-center gap-2 px-4 py-3.5 text-sm border-b-2 transition-all whitespace-nowrap"
              style={{
                borderColor: activeTab === id ? 'var(--lux-gold)' : 'transparent',
                color: activeTab === id ? 'var(--lux-gold)' : 'var(--lux-text-muted)',
              }}
              data-testid={`tab-${id}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8">

        {/* TENANT TAB */}
        {activeTab === 'tenants' && (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                {[
                  { label: 'Tenant Totali', value: stats.total_tenants, icon: Globe },
                  { label: 'Tenant Attivi', value: stats.active_tenants, icon: Check },
                  { label: 'Prenotazioni', value: stats.total_bookings_platform, icon: BarChart3 },
                  { label: 'Piani Attivi', value: Object.keys(stats.plans_distribution || {}).length, icon: Shield },
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={14} style={{ color: 'var(--lux-gold)' }} />
                      <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--lux-text-muted)' }}>{label}</p>
                    </div>
                    <p className="font-serif text-3xl font-light" style={{ color: 'var(--lux-text)' }}>{value}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Plans distribution */}
            {stats?.plans_distribution && Object.keys(stats.plans_distribution).length > 0 && (
              <div className="mb-10 p-5 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
                <p className="text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--lux-gold)' }}>Distribuzione piani</p>
                <div className="flex gap-4 flex-wrap">
                  {Object.entries(stats.plans_distribution).map(([plan, count]) => (
                    <div key={plan} className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: PLAN_COLORS[plan] || '#888' }} />
                      <span className="text-sm" style={{ color: 'var(--lux-text-sec)' }}>{PLAN_LABELS[plan] || plan}: <strong>{count}</strong></span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tenant list */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl font-light">Tenant / Clienti</h2>
              <button onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium"
                style={{ background: 'var(--lux-gold)', color: '#000' }}>
                <Plus size={15} /> Nuovo Tenant
              </button>
            </div>

            <div className="space-y-3">
              {(tenants || []).map(t => (
                <TenantRow
                  key={t.slug}
                  tenant={t}
                  storedKey={storedKey}
                  onRefresh={() => loadData(storedKey)}
                  onEdit={() => setEditTenant(t)}
                />
              ))}
              {(tenants || []).length === 0 && (
                <div className="text-center py-12" style={{ color: 'var(--lux-text-muted)' }}>
                  <Users size={32} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Nessun tenant. Crea il primo cliente.</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* LEADS CRM TAB */}
        {activeTab === 'leads' && (
          <LeadsCrmTab storedKey={storedKey} onComposeToLead={handleComposeToLead} />
        )}

        {/* MARKETING TAB */}
        {activeTab === 'marketing' && (
          <MarketingTab
            storedKey={storedKey}
            preselectedLead={preselectedLead}
            onClearPreselect={() => setPreselectedLead(null)}
          />
        )}

        {/* BILLING TAB */}
        {activeTab === 'billing' && <BillingTab storedKey={storedKey} />}

        {/* FEATURE FLAGS TAB */}
        {activeTab === 'features' && <FeatureFlagsTab storedKey={storedKey} />}

        {/* SYSTEM MONITOR TAB */}
        {activeTab === 'system' && <SystemTab storedKey={storedKey} />}

        {/* SUPERADMIN USERS TAB */}
        {activeTab === 'sa_users' && <AdminUsersTab storedKey={storedKey} />}

        {/* SETTINGS AI TAB */}
        {activeTab === 'settings' && <SettingsTab storedKey={storedKey} />}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && <AnalyticsTab storedKey={storedKey} />}

        {/* CERTIFIED PARTNERS TAB */}
        {activeTab === 'certified' && <CertifiedBrandsTab storedKey={storedKey} />}

        {/* MARKETPLACE TAB */}
        {activeTab === 'marketplace' && <MarketplaceTab token={storedKey} />}

        {/* WHITE-LABEL TAB */}
        {activeTab === 'whitelabel' && <WhitelabelTab token={storedKey} />}

      </div>

      {/* Create modal */}
      {showCreate && (
        <CreateTenantModal
          plans={plans}
          storedKey={storedKey}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadData(storedKey); }}
        />
      )}

      {/* Edit modal */}
      {editTenant && (
        <EditTenantModal
          tenant={editTenant}
          plans={plans}
          storedKey={storedKey}
          onClose={() => setEditTenant(null)}
          onSaved={() => { setEditTenant(null); loadData(storedKey); }}
        />
      )}
    </div>
  );
}

/* ── Login Screen ─────────────────────────────────────────────────────────── */
function LoginScreen({ email, setEmail, keyVal, setKey, onLogin, error, loading }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: 'var(--lux-bg)' }}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-sm flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--lux-gold)' }}>
            <Lock size={22} color="#000" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-3xl font-light" style={{ color: 'var(--lux-text)' }}>Shangrilux</h1>
          <p className="text-xs uppercase tracking-[0.3em] mt-1" style={{ color: 'var(--lux-gold)' }}>SuperAdmin</p>
        </div>
        <div className="p-8 rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--lux-text-muted-2)' }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="info@francescorusso.work"
                className="w-full rounded-sm px-4 py-3 text-sm"
                style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}
                data-testid="superadmin-email-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--lux-text-muted-2)' }}>Password</label>
              <input
                type="password"
                value={keyVal}
                onChange={e => setKey(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && onLogin()}
                placeholder="••••••••••••••••"
                className="w-full rounded-sm px-4 py-3 text-sm"
                style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}
                data-testid="superadmin-key-input"
              />
            </div>
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <button onClick={onLogin} disabled={loading}
              className="w-full py-3 text-sm font-medium flex items-center justify-center gap-2"
              style={{ background: 'var(--lux-gold)', color: '#000' }}
              data-testid="superadmin-login-btn">
              {loading ? <Loader2 size={15} className="animate-spin" /> : <ChevronRight size={15} />}
              Accedi al pannello
            </button>
          </div>
        </div>
        <p className="text-center text-xs mt-6" style={{ color: 'var(--lux-text-muted-3)' }}>
          Sviluppato da{' '}
          <a href="https://francescorusso.work" target="_blank" rel="noreferrer"
            className="underline" style={{ color: 'var(--lux-text-muted-2)' }}>
            Francesco Russo
          </a>
        </p>
      </div>
    </div>
  );
}

/* ── Tenant Row ─────────────────────────────────────────────────────────── */
function TenantRow({ tenant, storedKey, onRefresh, onEdit }) {
  const [deleting, setDeleting] = useState(false);
  const [showAccess, setShowAccess] = useState(false);

  const deleteTenant = async () => {
    if (!window.confirm(`Eliminare il tenant "${tenant.slug}"? Questa azione è irreversibile.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/tenants/${tenant.slug}`, { headers: { 'X-Superadmin-Key': storedKey } });
      toast.success('Tenant eliminato.');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante l\'eliminazione.');
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async () => {
    try {
      await axios.put(`${API}/tenants/${tenant.slug}`, { is_active: !tenant.is_active }, { headers: { 'X-Superadmin-Key': storedKey } });
      toast.success(tenant.is_active ? 'Tenant disattivato.' : 'Tenant attivato.');
      onRefresh();
    } catch {
      toast.error('Errore.');
    }
  };

  return (
    <div className="flex items-center gap-4 p-5 rounded-sm transition-all"
      style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
      {/* Active indicator */}
      <div className={`w-2 h-2 rounded-full shrink-0 ${tenant.is_active ? 'bg-green-500' : 'bg-gray-500'}`} />

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-0.5">
          <span className="font-medium" style={{ color: 'var(--lux-text)' }}>{tenant.name}</span>
          <span className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ background: `${PLAN_COLORS[tenant.plan] || '#888'}22`, color: PLAN_COLORS[tenant.plan] || '#888' }}>
            {PLAN_LABELS[tenant.plan] || tenant.plan}
          </span>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>slug: <code>{tenant.slug}</code></span>
          <span className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>db: <code>{tenant.db_name}</code></span>
          {tenant.owner_email && (
            <span className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>{tenant.owner_email}</span>
          )}
          {tenant.created_at && (
            <span className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
              {new Date(tenant.created_at).toLocaleDateString('it-IT')}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        <a href={`/panel?tenant=${tenant.slug}`} target="_blank" rel="noreferrer"
          title="Apri pannello tenant"
          className="p-2 rounded transition-colors"
          style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)' }}>
          <ExternalLink size={14} />
        </a>
        <button onClick={() => setShowAccess(true)} title="Gestisci accessi (credenziali cliente)"
          data-testid={`manage-access-btn-${tenant.slug}`}
          className="p-2 rounded transition-colors"
          style={{ background: 'rgba(201,168,76,0.12)', color: '#c9a84c' }}>
          <Key size={14} />
        </button>
        <button onClick={onEdit} title="Modifica"
          className="p-2 rounded transition-colors"
          style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted)' }}>
          <Edit3 size={14} />
        </button>
        <button onClick={toggleActive} title={tenant.is_active ? 'Disattiva' : 'Attiva'}
          className="p-2 rounded transition-colors"
          style={{ background: 'var(--lux-surface-3)', color: tenant.is_active ? 'var(--lux-gold)' : 'var(--lux-text-muted-3)' }}>
          {tenant.is_active ? <Check size={14} /> : <X size={14} />}
        </button>
        {tenant.slug !== 'shangrilux' && (
          <button onClick={deleteTenant} disabled={deleting} title="Elimina"
            className="p-2 rounded transition-colors"
            style={{ background: 'var(--lux-surface-3)', color: '#ef4444' }}>
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        )}
      </div>
      {showAccess && (
        <TenantAccessModal tenant={tenant} storedKey={storedKey} onClose={() => setShowAccess(false)} />
      )}
    </div>
  );
}

/* ── Create Tenant Modal ─────────────────────────────────────────────────── */
function CreateTenantModal({ plans, storedKey, onClose, onCreated }) {
  const [form, setForm] = useState({
    slug: '', name: '', name_en: '', plan: 'essenziale',
    owner_name: '', owner_email: '', domain: '', notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Auto-generate slug from name
  useEffect(() => {
    if (form.name && !form.slug) {
      set('slug', form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
    }
  }, [form.name]);

  const submit = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/tenants`, form, { headers: { 'X-Superadmin-Key': storedKey } });
      toast.success(`Tenant "${form.name}" creato.`);
      onCreated();
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore durante la creazione.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Nuovo Tenant" onClose={onClose}>
      <div className="space-y-4">
        <MField label="Nome struttura (IT)" value={form.name} onChange={v => set('name', v)} placeholder="es. Villa Belvedere" />
        <MField label="Nome struttura (EN)" value={form.name_en} onChange={v => set('name_en', v)} placeholder="es. Villa Belvedere" />
        <MField label="Slug (identificativo URL)" value={form.slug} onChange={v => set('slug', v.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="villa-belvedere" />
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'var(--lux-text-muted-2)' }}>Piano</label>
          <select value={form.plan} onChange={e => set('plan', e.target.value)}
            className="w-full rounded-sm px-4 py-3 text-sm"
            style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}>
            {Object.keys(plans).map(p => <option key={p} value={p}>{PLAN_LABELS[p] || p}</option>)}
          </select>
        </div>
        <MField label="Nome proprietario" value={form.owner_name} onChange={v => set('owner_name', v)} placeholder="Mario Rossi" />
        <MField label="Email proprietario" type="email" value={form.owner_email} onChange={v => set('owner_email', v)} placeholder="mario@struttura.com" />
        <MField label="Dominio custom (opzionale — solo white-label)" value={form.domain} onChange={v => set('domain', v)} placeholder="es. villa-belvedere.com" />
        <MField label="Note interne" value={form.notes} onChange={v => set('notes', v)} placeholder="Onboarding, note fatturazione..." />
        {error && <p className="text-red-400 text-xs">{error}</p>}
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-3 text-sm" style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>Annulla</button>
          <button onClick={submit} disabled={loading} className="flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2" style={{ background: 'var(--lux-gold)', color: '#000' }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Crea Tenant
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ── Edit Tenant Modal ─────────────────────────────────────────────────── */
function EditTenantModal({ tenant, plans, storedKey, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: tenant.name || '',
    name_en: tenant.name_en || '',
    plan: tenant.plan || 'essenziale',
    owner_name: tenant.owner_name || '',
    owner_email: tenant.owner_email || '',
    domain: tenant.domain || '',
    notes: tenant.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.put(`${API}/tenants/${tenant.slug}`, form, { headers: { 'X-Superadmin-Key': storedKey } });
      toast.success('Tenant aggiornato.');
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore durante l\'aggiornamento.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={`Modifica: ${tenant.slug}`} onClose={onClose}>
      <div className="space-y-4">
        <MField label="Nome (IT)" value={form.name} onChange={v => set('name', v)} />
        <MField label="Nome (EN)" value={form.name_en} onChange={v => set('name_en', v)} />
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'var(--lux-text-muted-2)' }}>Piano</label>
          <select value={form.plan} onChange={e => set('plan', e.target.value)}
            className="w-full rounded-sm px-4 py-3 text-sm"
            style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }}>
            {Object.keys(plans).map(p => <option key={p} value={p}>{PLAN_LABELS[p] || p}</option>)}
          </select>
        </div>
        <MField label="Nome proprietario" value={form.owner_name} onChange={v => set('owner_name', v)} />
        <MField label="Email proprietario" type="email" value={form.owner_email} onChange={v => set('owner_email', v)} />
        <MField label="Dominio" value={form.domain} onChange={v => set('domain', v)} />
        <MField label="Note interne" value={form.notes} onChange={v => set('notes', v)} />
        {error && <p className="text-red-400 text-xs">{error}</p>}
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-3 text-sm" style={{ border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>Annulla</button>
          <button onClick={submit} disabled={loading} className="flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2" style={{ background: 'var(--lux-gold)', color: '#000' }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Salva
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ── Generic Modal ─────────────────────────────────────────────────────── */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-lg rounded-sm" style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--lux-border-1)' }}>
          <h3 className="font-serif text-lg font-light" style={{ color: 'var(--lux-text)' }}>{title}</h3>
          <button onClick={onClose} style={{ color: 'var(--lux-text-muted)' }}><X size={18} /></button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function MField({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: 'var(--lux-text-muted-2)' }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full rounded-sm px-4 py-3 text-sm"
        style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />
    </div>
  );
}
